/* eslint-disable react/no-unstable-nested-components */
import {
  DragDropContext,
  Draggable,
  Droppable,
} from '@atlaskit/pragmatic-drag-and-drop-react-beautiful-dnd-migration'
import { XMarkIcon } from '@heroicons/react/24/outline'
import * as _ from 'lodash-es'
import { observer } from 'mobx-react'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate, useOutletContext, useParams, useLocation } from 'react-router-dom'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import { TextInput } from '@/components/forms/TextInput'
import {
  AddIcon,
  DownloadIcon,
  MinusIcon,
  SearchIcon,
  SquaresIcon,
  TableIcon,
} from '@/components/icons'
import { CollapsibleSection } from '@/components/shared/CollapsibleSection'
import { DataTable, getSortProps } from '@/components/shared/DataTable'
import { FilterPopover } from '@/components/shared/FilterPopover'
import { OUTSTANDING_CHECKBOX_FILTERS } from '@/components/shared/FilterPopover/constants'
import { Highlights } from '@/components/shared/Highlights'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { Pagination } from '@/components/ui/Pagination'
import { StateContainer } from '@/components/ui/StateContainer'
import useAbortControllers from '@/hooks/AbortControllers'
import useHoverItem from '@/hooks/HoverItem'
import {
  createRepairOrderExport,
  getActionsNeeded,
  getAdvisors,
  getRepairOrderExport,
  getRepairOrderKpis,
  getRepairOrders,
  getTechnicians,
} from '@/services/dealer.service'
import { updateRepairOrder } from '@/services/repairOrders.service'
import { DealerFilterStoreContext } from '@/stores/DealerFilterStore'
import { DisplayStoreContext } from '@/stores/DisplayStore'
import { NotificationStoreContext } from '@/stores/NotificationStore'
import { TaskStoreContext } from '@/stores/TaskStore'
import { UserStoreContext } from '@/stores/UserStore'
import { setDealerGroup, track } from '@/utils/analytics'
import {
  EXPORT_INITIATED,
  FILTER_APPLIED,
  FILTER_CLEARED,
  RO_DRAGGED,
  SEARCH_PERFORMED,
  SORT_CHANGED,
  VIEW_MODE_ACTIVE,
} from '@/utils/analytics.constants'
import { handlePagination, toast } from '@/utils/helpers'

import ExpandableRow from './components/ExpandableRow'
import RepairOrderCard from './components/RepairOrderCard'
import RepairOrderNumberCell from './components/RepairOrderNumberCell'
import { COLUMNS, DEFAULT_FILTERS } from './constants'
import { ResolvedROs } from './sections/ResolvedROs'
import { configureUrlParams } from './utils'

const DealerDetails = observer(() => {
  // Context
  const navigate = useNavigate()
  const { id } = useParams()
  const { dealer: layoutDealer, error: layoutError } = useOutletContext()
  const { createNewTask } = useContext(TaskStoreContext)
  const { isDealerAdmin, isDealerUser, isImpersonating, isWpcUser, isReadOnlySession } =
    useContext(UserStoreContext)

  const { activeView, setActiveView } = useContext(DisplayStoreContext)
  const dealerFilterStore = useContext(DealerFilterStoreContext)
  const { unreadNotificationsCount } = useContext(NotificationStoreContext)

  const location = useLocation()

  // Cancels superseded list/KPI/filter requests so the last one *requested*
  // wins, not the last one to *resolve* (dealer switch + pagination races).
  const { getSignal } = useAbortControllers()

  // If the user is a WPC user or if they are a Dealer Admin, they are on the admin side of the
  // repair order and have the same permissions. This is to encorporate Dealer Managed workflows.
  const isROAdminUser = isWpcUser || isDealerAdmin
  const isROServiceUser = isDealerUser && !isDealerAdmin

  // State
  const [, setFirstLoad] = useState(true)
  const [loadingOutstanding, setLoadingOutstanding] = useState(true)
  const [error, setError] = useState(null)
  const [updatedDealer, setUpdatedDealer] = useState(location.state?.dealer || null)
  const [kpis, setKpis] = useState([])
  const [search, setSearch] = useState(null)
  const [searchTerm, setSearchTerm] = useState(null)
  const [loadingExport, setLoadingExport] = useState(false)
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false)
  const [repairOrderPayload, setRepairOrderPayload] = useState(false)
  const [selectedRepairOrderId, setSelectedRepairOrderId] = useState(false)

  // Filters
  const [actionsNeeded, setActionsNeeded] = useState({
    unresolved: [],
  })
  const [advisors, setAdvisors] = useState({
    unresolved: [],
  })
  const [technicians, setTechnicians] = useState({
    unresolved: [],
  })
  const initialOutstandingFilters =
    dealerFilterStore.currentDealerId === id
      ? dealerFilterStore.outstandingFilters
      : DEFAULT_FILTERS
  const [selectedFilters, setSelectedFilters] = useState(initialOutstandingFilters)

  // Admin Response — init from store if same dealer
  const isSameDealer = dealerFilterStore.currentDealerId === id
  const [draftRepairOrders, setDraftRepairOrders] = useState([])
  const [draftTotalRows, setDraftTotalRows] = useState(0)
  const [draftPages, setDraftPages] = useState({ next: null, previous: null })
  const [draftPerPage, setDraftPerPage] = useState(10)
  const [draftCurrentPage, setDraftCurrentPage] = useState(1)
  const [draftSortedColumn, setDraftSortedColumn] = useState('-created_at')
  const [draftSearch, setDraftSearch] = useState(null)
  const [draftSearchTerm, setDraftSearchTerm] = useState('')
  const draftSearchInputRef = useRef(null)
  const [adminRepairOrders, setAdminRepairOrders] = useState([])
  const [adminCurrentPage, setAdminCurrentPage] = useState(
    isSameDealer ? dealerFilterStore.adminCurrentPage : 1,
  )
  const [adminPerPage, setAdminPerPage] = useState(
    isSameDealer ? dealerFilterStore.adminPerPage : 20,
  )
  const [adminTotalRows, setAdminTotalRows] = useState()
  const [adminPages, setAdminPages] = useState(null)
  const [adminSortedColumn, setAdminSortedColumn] = useState(
    isSameDealer ? dealerFilterStore.adminSortedColumn : 'ro_number',
  )

  // Dealer Response — init from store if same dealer
  const [dealerRepairOrders, setDealerRepairOrders] = useState([])
  const [dealerCurrentPage, setDealerCurrentPage] = useState(
    isSameDealer ? dealerFilterStore.dealerCurrentPage : 1,
  )
  const [dealerPerPage, setDealerPerPage] = useState(
    isSameDealer ? dealerFilterStore.dealerPerPage : 20,
  )
  const [dealerTotalRows, setDealerTotalRows] = useState()
  const [dealerPages, setDealerPages] = useState(null)
  const [dealerSortedColumn, setDealerSortedColumn] = useState(
    isSameDealer ? dealerFilterStore.dealerSortedColumn : 'ro_number',
  )

  // Refs
  const searchInputRef = useRef()
  const initialLoadRef = useRef(true)
  const isAdminInitialMount = useRef(true)
  const isDealerInitialMount = useRef(true)

  const handleErrors = (m) => toast(m, 'error')
  const handleSuccess = (message) => toast(message, 'success')

  const RO_BASE_URL = `/dealers/${id}/repair-orders/?expand=line_items,line_items.actions_needed,line_items.actions_needed.action`
  const KPI_BASE_URL = `/dealers/${id}/repair-orders/kpis/`

  /**
   * Fetches the repair orders based on the specified `url` and `state`.
   * @param {string} url
   * @param {string} state
   * @param {boolean} silent - Set to true to suppress loading states.
   */
  const getUpdatedRepairOrders = async (url, state = '', silent = false) => {
    let limit = ''
    if (state === 'Draft') {
      limit = draftPerPage
    } else if (state === 'WPC Review') {
      limit = adminPerPage
    } else if (state === 'Dealer Review') {
      limit = dealerPerPage
    }

    const setLoading = silent ? () => {} : setLoadingOutstanding

    const response = await getRepairOrders(
      `${url}&state=${state}&limit=${limit}`,
      setLoading,
      setError,
      () => {},
      getSignal(`repair-orders:${state}`),
    )

    if (response) {
      if (state === 'Draft') {
        setDraftRepairOrders(response.results)
        setDraftTotalRows(response.count)
        setDraftPages({ next: response.next, previous: response.previous })
      } else if (state === 'WPC Review') {
        setAdminRepairOrders(response.results)
        setAdminTotalRows(response.count)
        setAdminPages({ next: response.next, previous: response.previous })
      } else if (state === 'Dealer Review') {
        setDealerRepairOrders(response.results)
        setDealerTotalRows(response.count)
        setDealerPages({ next: response.next, previous: response.previous })
      }
    }

    return response
  }

  /**
   * Gets the updated repair order KPIs.
   * @param {string} url
   */
  const getUpdatedRepairOrderKpis = async (url) => {
    const response = await getRepairOrderKpis(url, () => {}, handleErrors, getSignal('kpis'))

    if (response) {
      // Transform histogram into the format we need
      const histogram = _.map(response.roAgeHistogram, (value, key) => ({
        days: key,
        age: value,
      }))

      setKpis({
        ...response,
        roAgeHistogram: histogram,
      })
    }
  }

  // Track active view mode on page load
  useEffect(() => {
    track(VIEW_MODE_ACTIVE, { view: activeView, dealer_id: id })
  }, [id])

  useEffect(() => {
    setError(null)

    // Reset KPIs when the dealer is changed
    setKpis([])

    const getUpdatedData = async () => {
      const [updatedActions, updatedAdvisors, updatedTechnicians] = await Promise.all([
        // Unresolved Filter Options
        getActionsNeeded(id, false, () => {}, handleErrors, getSignal('actions')),
        getAdvisors(id, false, () => {}, handleErrors, getSignal('advisors')),
        getTechnicians(id, false, () => {}, handleErrors, getSignal('technicians')),
      ])

      setActionsNeeded({
        unresolved: _.map(updatedActions, (a) => ({ id: a.id, label: a.name })),
      })
      setAdvisors({
        unresolved: _.map(updatedAdvisors, (a) => ({ id: a, label: a })),
      })
      setTechnicians({
        unresolved: _.map(updatedTechnicians, (t) => ({ id: t, label: t })),
      })

      setFirstLoad(false)
    }

    getUpdatedData()
  }, [id])

  // Sync dealer from layout context
  useEffect(() => {
    if (layoutDealer) {
      setUpdatedDealer(layoutDealer)
      setDealerGroup(layoutDealer)
    }
  }, [layoutDealer])

  // Set dealer context and reset filters if navigating to a different dealer
  useEffect(() => {
    if (dealerFilterStore.currentDealerId !== id) {
      dealerFilterStore.clearContext()
      dealerFilterStore.setDealerContext(id)
    }
  }, [id])

  // Sync filter changes to store for persistence
  useEffect(() => {
    dealerFilterStore.setOutstandingFilters(selectedFilters)
  }, [selectedFilters])

  // Sync sort/pagination to store for persistence
  useEffect(() => {
    dealerFilterStore.setAdminSortedColumn(adminSortedColumn)
  }, [adminSortedColumn])
  useEffect(() => {
    dealerFilterStore.setAdminCurrentPage(adminCurrentPage)
  }, [adminCurrentPage])
  useEffect(() => {
    dealerFilterStore.setAdminPerPage(adminPerPage)
  }, [adminPerPage])
  useEffect(() => {
    dealerFilterStore.setDealerSortedColumn(dealerSortedColumn)
  }, [dealerSortedColumn])
  useEffect(() => {
    dealerFilterStore.setDealerCurrentPage(dealerCurrentPage)
  }, [dealerCurrentPage])
  useEffect(() => {
    dealerFilterStore.setDealerPerPage(dealerPerPage)
  }, [dealerPerPage])

  /**
   * Configure the URL parameters based on the search term and filters.
   */
  const urlParams = useMemo(
    () => configureUrlParams(search, selectedFilters),
    [search, selectedFilters],
  )

  const adminStatusPrefix = useMemo(() => {
    if (!updatedDealer) return ''
    return isDealerUser && updatedDealer?.isDealerManaged ? 'Admin' : 'WPC'
  }, [updatedDealer, isDealerAdmin])

  // Who can create + see Draft ROs on this dealer: WPC users on any dealer, plus
  // dealer admins on self-managed dealers. RO building is admin-driven, matching the
  // backend RepairOrder/LineItem "add" perms (user_can_manage_ros = WPC or dealer admin).
  const canManageDraftRos = useMemo(
    () => isWpcUser || (isDealerAdmin && !!updatedDealer?.isDealerManaged),
    [isWpcUser, isDealerAdmin, updatedDealer],
  )

  /**
   * Silently fetch updated repair orders when the user's unread notifications count changes.
   */
  useEffect(() => {
    // Skip the first render since the other useEffect will handle it
    if (initialLoadRef.current) {
      initialLoadRef.current = false
      return
    }

    const params = urlParams === '' ? '' : `&${urlParams}`
    getUpdatedRepairOrders(
      `${RO_BASE_URL}&page=1&order_by=${adminSortedColumn}${params}`,
      'WPC Review',
      true,
    )
    getUpdatedRepairOrders(
      `${RO_BASE_URL}&page=1&order_by=${dealerSortedColumn}${params}`,
      'Dealer Review',
      true,
    )
    if (canManageDraftRos) {
      getUpdatedRepairOrders(`${RO_BASE_URL}&page=1&order_by=${draftSortedColumn}`, 'Draft', true)
    }
  }, [unreadNotificationsCount])

  /**
   * Gets updated repair orders that are in Admin review.
   */
  useEffect(() => {
    const page = isAdminInitialMount.current ? adminCurrentPage : 1
    if (!isAdminInitialMount.current) setAdminCurrentPage(1)
    isAdminInitialMount.current = false
    const params = urlParams === '' ? '' : `&${urlParams}`
    getUpdatedRepairOrders(
      `${RO_BASE_URL}&page=${page}&order_by=${adminSortedColumn}${params}`,
      'WPC Review',
    ).then((response) => {
      if (search && response) {
        track(SEARCH_PERFORMED, { context: 'dealer_detail', has_results: response.count > 0 })
      }
    })
  }, [id, urlParams, adminPerPage, adminSortedColumn])

  /**
   * Gets draft repair orders (WPC users + self-managed dealer admins).
   */
  useEffect(() => {
    if (canManageDraftRos) {
      const searchParam = draftSearch ? `&q=${draftSearch}` : ''
      getUpdatedRepairOrders(
        `${RO_BASE_URL}&page=${draftCurrentPage}&order_by=${draftSortedColumn}${searchParam}`,
        'Draft',
      )
    }
  }, [id, draftPerPage, draftSortedColumn, draftSearch, canManageDraftRos])

  /**
   * Gets updated kpis when url params change or the dealer id.
   */
  useEffect(() => {
    getUpdatedRepairOrderKpis(`${KPI_BASE_URL}?${urlParams}`)
  }, [id, urlParams])

  /**
   * Gets updated repair orders that are in Dealer review.
   */
  useEffect(() => {
    const page = isDealerInitialMount.current ? dealerCurrentPage : 1
    if (!isDealerInitialMount.current) setDealerCurrentPage(1)
    isDealerInitialMount.current = false
    const params = urlParams === '' ? '' : `&${urlParams}`
    getUpdatedRepairOrders(
      `${RO_BASE_URL}&page=${page}&order_by=${dealerSortedColumn}${params}`,
      'Dealer Review',
    )
  }, [id, urlParams, dealerPerPage, dealerSortedColumn])

  /**
   * Debounces the search input.
   */
  const searchRepairOrders = useCallback(
    _.debounce((term) => {
      setSearch(term)
    }, 500),
    [],
  )
  const searchDraftRepairOrders = useCallback(_.debounce(setDraftSearch, 500), [])

  /**
   * Renders the dealer datatable.
   */
  const renderWaitingOnDealerResponseTable = () => (
    <DataTable
      onRowClicked={(row) =>
        navigate(`/dealer/${row.dealer}/repair-order/${row.id}`, {
          state: { dealer: updatedDealer },
        })
      }
      highlightOnHover
      columns={updatedColumns}
      data={dealerRepairOrders}
      {...getSortProps(dealerSortedColumn, updatedColumns)}
      expandableIcon={{
        collapsed: (
          <div className="flex flex-row gap-2">
            <div className="bg-blue-dark h-[38px] w-2 rounded-r-[4px]" />
            <AddIcon className="h-4 stroke-blue-800" />
          </div>
        ),
        expanded: (
          <div className="flex flex-row gap-3">
            <div className="bg-blue-dark h-[38px] w-2 rounded-r-[4px]" />
            <MinusIcon className="h-4 stroke-blue-800" />
          </div>
        ),
      }}
      expandableRows
      expandableRowsComponent={(props) => <ExpandableRow data={props.data} />}
      onChangePage={(page) =>
        handlePagination(
          page,
          dealerCurrentPage,
          dealerPerPage,
          dealerTotalRows,
          dealerPages,
          setDealerCurrentPage,
          (url) => getUpdatedRepairOrders(url, 'Dealer Review'),
          RO_BASE_URL,
          `order_by=${dealerSortedColumn}${urlParams === '' ? '' : `&${urlParams}`}`,
        )
      }
      onChangeRowsPerPage={(currentRowsPerPage) => setDealerPerPage(currentRowsPerPage)}
      onSort={(column, direction) => {
        track(SORT_CHANGED, { context: 'dealer_detail', column: column.sortBy, direction })
        const d = direction === 'asc' ? '' : '-'
        setDealerSortedColumn(`${d}${column.sortBy}`)
      }}
      pagination
      paginationServer
      paginationDefaultPage={dealerCurrentPage}
      paginationPerPage={dealerPerPage}
      paginationRowsPerPageOptions={[10, 20, 30, 50]}
      paginationTotalRows={dealerTotalRows}
      progressPending={loadingOutstanding}
      sortServer
    />
  )

  // Update hovered state of RO #
  const { hoveredItemId, handleMouseEnter, handleMouseLeave } = useHoverItem()

  const updatedColumns = COLUMNS.map((column) => {
    if (column.name === 'RO #') {
      return {
        ...column,
        cell: (row) => (
          <RepairOrderNumberCell
            row={row}
            hoveredRowId={hoveredItemId}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
        ),
      }
    }
    return column
  })

  /**
   * Renders the Admin datatable.
   */
  const renderWaitingOnWpcResponseTable = () => (
    <DataTable
      onRowClicked={(row) =>
        navigate(`/dealer/${row.dealer}/repair-order/${row.id}`, {
          state: { dealer: updatedDealer },
        })
      }
      highlightOnHover
      columns={updatedColumns}
      data={adminRepairOrders}
      {...getSortProps(adminSortedColumn, updatedColumns)}
      expandableIcon={{
        collapsed: (
          <div className="flex flex-row gap-2">
            <div className="bg-gold-dark h-[38px] w-2 rounded-r-[4px]" />
            <AddIcon className="h-4 stroke-blue-800" />
          </div>
        ),
        expanded: (
          <div className="flex flex-row gap-3">
            <div className="bg-gold-dark h-[38px] w-2 rounded-r-[4px]" />
            <MinusIcon className="h-4 stroke-blue-800" />
          </div>
        ),
      }}
      expandableRows
      expandableRowsComponent={(props) => <ExpandableRow data={props.data} />}
      onChangePage={(page) =>
        handlePagination(
          page,
          adminCurrentPage,
          adminPerPage,
          adminTotalRows,
          adminPages,
          setAdminCurrentPage,
          (url) => getUpdatedRepairOrders(url, 'WPC Review'),
          RO_BASE_URL,
          `order_by=${adminSortedColumn}${urlParams === '' ? '' : `&${urlParams}`}`,
        )
      }
      onChangeRowsPerPage={(currentRowsPerPage) => setAdminPerPage(currentRowsPerPage)}
      onSort={(column, direction) => {
        track(SORT_CHANGED, { context: 'dealer_detail', column: column.sortBy, direction })
        const d = direction === 'asc' ? '' : '-'
        setAdminSortedColumn(`${d}${column.sortBy}`)
      }}
      pagination
      paginationServer
      paginationDefaultPage={adminCurrentPage}
      paginationPerPage={adminPerPage}
      paginationRowsPerPageOptions={[10, 20, 30, 50]}
      paginationTotalRows={adminTotalRows}
      progressPending={loadingOutstanding}
      sortServer
    />
  )

  /**
   * Handles pagination for the dealer review repair orders.
   * @param {number} page
   */
  const handleDealerReviewPagination = (page) => {
    handlePagination(
      page,
      dealerCurrentPage,
      dealerPerPage,
      dealerTotalRows,
      dealerPages,
      setDealerCurrentPage,
      (url) => getUpdatedRepairOrders(url, 'Dealer Review'),
      RO_BASE_URL,
      `order_by=${dealerSortedColumn}${urlParams === '' ? '' : `&${urlParams}`}`,
    )
  }

  /**
   * Handles pagination for the Admin review repair orders.
   * @param {number} page
   */
  const handleAdminReviewPagination = (page) => {
    handlePagination(
      page,
      adminCurrentPage,
      adminPerPage,
      adminTotalRows,
      adminPages,
      setAdminCurrentPage,
      (url) => getUpdatedRepairOrders(url, 'WPC Review'),
      RO_BASE_URL,
      `order_by=${adminSortedColumn}${urlParams === '' ? '' : `&${urlParams}`}`,
    )
  }

  /**
   * Handles updating repair order status once the user has finished dragging the item.
   * @param {object} result
   * @returns
   */
  const onDragEnd = async ({ source, destination, draggableId }) => {
    // Check if the drag resulted in a valid drop (not outside of any droppable) and if the user is a dealer user
    if (!destination || isROServiceUser) {
      return
    }

    // Determine the movement direction and set payload accordingly
    let payload = {}
    if (source.droppableId === 'columnA' && destination.droppableId === 'columnB') {
      // Item moved from column A to column B
      payload = { markAsDealerReview: true }
    } else if (source.droppableId === 'columnB' && destination.droppableId === 'columnA') {
      // Item moved from column B to column A
      payload = { markAsWpcReview: true }
    } else {
      // If the item is not moving between the specified columns, do nothing
      return
    }

    track(RO_DRAGGED, {
      dealer_id: id,
      ro_id: draggableId,
      from_column: source.droppableId === 'columnA' ? 'requires_review' : 'waiting_on_response',
      to_column: destination.droppableId === 'columnA' ? 'requires_review' : 'waiting_on_response',
    })

    setRepairOrderPayload(payload)
    setSelectedRepairOrderId(draggableId)
    setIsConfirmationModalVisible(true)
  }

  /**
   * Renders a subset of cards based on whether the index is even or odd.
   *
   * @param {Array} data - The array of data items to map through.
   * @param {boolean} isEven - True to filter even indices, false for odd indices.
   * @param {boolean} requiresReview - Whether to include the 'requiresReview' prop.
   * @returns JSX elements of filtered cards.
   */
  const renderFilteredCards = (data, isEven, requiresReview = false, isClickable = false) =>
    _.map(
      data,
      (item, index) =>
        (isEven ? index % 2 === 0 : index % 2 !== 0) && (
          <Draggable
            draggableId={item.id}
            key={item.id}
            index={index}
            isDragDisabled={isROServiceUser}
          >
            {(provided) => (
              <div
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                style={{
                  ...provided.draggableProps.style,
                  cursor: isROServiceUser ? 'default' : 'grab',
                }}
                ref={provided.innerRef}
              >
                <RepairOrderCard
                  key={`column-${isEven ? 'even' : 'odd'}-${item.id}`}
                  item={item}
                  hoveredItemId={hoveredItemId}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  requiresReview={requiresReview}
                  isClickable={isClickable}
                />
              </div>
            )}
          </Draggable>
        ),
    )

  const renderRequiresReviewCards = () => {
    if (
      (isROServiceUser && dealerRepairOrders.length === 0) ||
      (isROAdminUser && adminRepairOrders.length === 0)
    ) {
      return (
        <div className="flex h-full w-full flex-col items-center justify-center">
          <span className="text-charcoal-400 text-lg">No repair orders found.</span>
        </div>
      )
    }

    return (
      <>
        <div className="w-full max-w-[380px] lg:w-1/2">
          {isROServiceUser
            ? renderFilteredCards(dealerRepairOrders, true, true, true)
            : renderFilteredCards(adminRepairOrders, true, true, true)}
        </div>

        <div className="w-full max-w-[380px] lg:w-1/2">
          {isROServiceUser
            ? renderFilteredCards(dealerRepairOrders, false, true, true)
            : renderFilteredCards(adminRepairOrders, false, true, true)}
        </div>
      </>
    )
  }

  const renderWaitingOnResponseCards = () => {
    if (
      (isROServiceUser && adminRepairOrders.length === 0) ||
      (isROAdminUser && dealerRepairOrders.length === 0)
    ) {
      return (
        <div className="flex h-full w-full items-center justify-center">
          <span className="text-charcoal-400 text-lg">No repair orders found.</span>
        </div>
      )
    }

    return (
      <>
        <div className="w-full max-w-[380px] lg:w-1/2">
          {isROServiceUser
            ? renderFilteredCards(adminRepairOrders, true, false)
            : renderFilteredCards(dealerRepairOrders, true, false)}
        </div>

        <div className="w-full max-w-[380px] lg:w-1/2">
          {isROServiceUser
            ? renderFilteredCards(adminRepairOrders, false, false)
            : renderFilteredCards(dealerRepairOrders, false, false)}
        </div>
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>{updatedDealer?.name || 'Dealer Details'}</title>
      </Helmet>

      <StateContainer error={error || layoutError}>
        <div className="flex w-full flex-col space-y-4">
          <Highlights
            kpis={kpis}
            highDollarThreshold={updatedDealer?.manufacturer?.highDollarThreshold}
            loading={loadingOutstanding}
          />

          {canManageDraftRos && (
            <div className="flex justify-end">
              <Button
                className="px-3 py-2"
                dataTestId="create-draft-repair-order"
                icon={<AddIcon className="h-4 stroke-[#ffffff]" />}
                label="Add Repair Order"
                onClick={() =>
                  navigate(`/dealer/${id}/repair-order/create`, {
                    state: { dealer: updatedDealer },
                  })
                }
                ariaLabel="Create New Repair Order"
                disabled={isReadOnlySession}
              />
            </div>
          )}

          {/* Draft ROs — WPC users + self-managed dealer admins */}
          {canManageDraftRos && (
            <CollapsibleSection title="Draft ROs">
              <div className="mb-4 flex flex-wrap items-center gap-4">
                <TextInput
                  className="w-72 pl-10"
                  data-testid="draft-search"
                  icon={<SearchIcon className="stroke-charcoal ml-2 w-5" />}
                  endIcon={
                    draftSearchTerm ? (
                      <button
                        type="button"
                        onClick={() => {
                          setDraftSearchTerm('')
                          searchDraftRepairOrders('')
                          draftSearchInputRef.current.value = ''
                        }}
                      >
                        <XMarkIcon className="text-midnight h-5" aria-hidden="true" />
                      </button>
                    ) : null
                  }
                  onChange={(e) => {
                    searchDraftRepairOrders(e.target.value)
                    setDraftSearchTerm(e.target.value)
                  }}
                  placeholder="Search"
                  value={draftSearchTerm}
                  ref={draftSearchInputRef}
                />

                <FilterPopover
                  filters={selectedFilters}
                  onFiltersChange={setSelectedFilters}
                  checkboxFilters={OUTSTANDING_CHECKBOX_FILTERS}
                  dropdownOptions={{
                    actionNeeded: actionsNeeded.unresolved,
                    advisor: advisors.unresolved,
                    technician: technicians.unresolved,
                  }}
                  anchor="bottom end"
                  testId="draft-filter"
                />
              </div>
              {draftTotalRows === 0 ? (
                <div className="text-charcoal-400 flex items-center justify-center pb-8 text-sm italic">
                  No draft repair orders. Create one to get started.
                </div>
              ) : (
                <DataTable
                  onRowClicked={(row) =>
                    navigate(`/dealer/${row.dealer}/repair-order/${row.id}`, {
                      state: { dealer: updatedDealer },
                    })
                  }
                  highlightOnHover
                  columns={updatedColumns}
                  data={draftRepairOrders}
                  {...getSortProps(draftSortedColumn, updatedColumns)}
                  expandableIcon={{
                    collapsed: (
                      <div className="flex flex-row gap-2">
                        <div className="bg-charcoal-300 h-[38px] w-2 rounded-r-[4px]" />
                        <AddIcon className="h-4 stroke-blue-800" />
                      </div>
                    ),
                    expanded: (
                      <div className="flex flex-row gap-3">
                        <div className="bg-charcoal-300 h-[38px] w-2 rounded-r-[4px]" />
                        <MinusIcon className="h-4 stroke-blue-800" />
                      </div>
                    ),
                  }}
                  expandableRows
                  expandableRowsComponent={(props) => <ExpandableRow data={props.data} />}
                  onChangePage={(page) =>
                    handlePagination(
                      page,
                      draftCurrentPage,
                      draftPerPage,
                      draftTotalRows,
                      draftPages,
                      setDraftCurrentPage,
                      (url) => getUpdatedRepairOrders(url, 'Draft'),
                      `${RO_BASE_URL}&limit=`,
                      `order_by=${draftSortedColumn}`,
                    )
                  }
                  onChangeRowsPerPage={(currentRowsPerPage) => setDraftPerPage(currentRowsPerPage)}
                  onSort={(column, direction) => {
                    const d = direction === 'asc' ? '' : '-'
                    setDraftSortedColumn(`${d}${column.sortBy}`)
                  }}
                  pagination
                  paginationServer
                  paginationDefaultPage={draftCurrentPage}
                  paginationPerPage={draftPerPage}
                  paginationRowsPerPageOptions={[10, 20, 30, 50]}
                  paginationTotalRows={draftTotalRows}
                  progressPending={loadingOutstanding}
                  sortServer
                />
              )}
            </CollapsibleSection>
          )}

          {/* Outstanding List ROs */}
          <CollapsibleSection title="Outstanding List ROs">
            <div className="text-gray-500">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-x-4">
                  <TextInput
                    className="w-72 pl-10"
                    data-testid="repair-search"
                    icon={<SearchIcon className="stroke-charcoal ml-2 w-5" />}
                    endIcon={
                      searchTerm ? (
                        <button
                          type="button"
                          onClick={() => {
                            setSearchTerm('')
                            searchRepairOrders('')
                            searchInputRef.current.value = ''
                          }}
                        >
                          <XMarkIcon className="text-midnight h-5" aria-hidden="true" />
                        </button>
                      ) : null
                    }
                    onChange={(e) => {
                      searchRepairOrders(e.target.value)
                      setSearchTerm(e.target.value)
                    }}
                    placeholder="Search"
                    value={searchTerm}
                    ref={searchInputRef}
                  />

                  <FilterPopover
                    filters={selectedFilters}
                    onFiltersChange={(newFilters) => {
                      // Deep equality, not JSON.stringify — key insertion order
                      // is fragile and can mislabel CLEARED as APPLIED.
                      const isClearing = _.isEqual(newFilters, DEFAULT_FILTERS)
                      if (isClearing) {
                        track(FILTER_CLEARED, { context: 'dealer_detail' })
                      } else {
                        const activeCount = Object.values(newFilters).filter((v) =>
                          Array.isArray(v) ? v.length > 0 : v === true,
                        ).length
                        track(FILTER_APPLIED, {
                          context: 'dealer_detail',
                          filter_count: activeCount,
                        })
                      }
                      setSelectedFilters(newFilters)
                    }}
                    checkboxFilters={OUTSTANDING_CHECKBOX_FILTERS}
                    dropdownOptions={{
                      actionNeeded: actionsNeeded.unresolved,
                      advisor: advisors.unresolved,
                      technician: technicians.unresolved,
                    }}
                    anchor="bottom end"
                  />
                </div>

                <div className="flex items-center">
                  <div className="flex flex-row gap-2">
                    <Button
                      className="px-1 py-2"
                      dataTestId="export-repair-orders"
                      disabled={loadingExport || isImpersonating}
                      label={<DownloadIcon className="h-4 stroke-[#ffffff]" />}
                      ariaLabel="Export Unresolved Repair Orders"
                      onClick={async () => {
                        track(EXPORT_INITIATED, { type: 'ro', dealer_id: id })
                        const baseParams = '?state=WPC Review&state=Dealer Review'
                        const params = urlParams === '' ? baseParams : `${baseParams}&${urlParams}`
                        const exportTask = await createRepairOrderExport(
                          `/dealers/${id}/repair-order-exports/${params}`,
                          handleErrors,
                          setLoadingExport,
                        )

                        if (exportTask) {
                          createNewTask('Download', exportTask, {
                            label: 'Repair Orders',
                            filename: `${updatedDealer.name} - Repair Orders.xlsx`,
                            checkUpdatedTask: async () => {
                              const updatedTask = await getRepairOrderExport(
                                id,
                                exportTask.id,
                                handleErrors,
                              )
                              return updatedTask
                            },
                          })
                        }
                      }}
                    />
                  </div>
                  <div
                    role="group"
                    aria-label="View options"
                    className="ml-4 flex items-center gap-2 rounded-xl bg-blue-800/10 p-1"
                  >
                    <button
                      type="button"
                      aria-pressed={activeView === 'grid'}
                      aria-label="Grid view"
                      className={`rounded-lg p-2 ${
                        activeView === 'grid' ? 'bg-white shadow-md' : 'bg-transparent'
                      }`}
                      onClick={() => {
                        setAdminSortedColumn('ages_out')
                        setDealerSortedColumn('ages_out')
                        setActiveView('grid')
                      }}
                    >
                      <SquaresIcon className="h-4 w-4 text-blue-800" />
                    </button>
                    <button
                      type="button"
                      aria-pressed={activeView === 'table'}
                      aria-label="Table view"
                      className={`rounded-lg p-2 ${
                        activeView === 'table' ? 'bg-white shadow-md' : 'bg-transparent'
                      }`}
                      onClick={() => setActiveView('table')}
                    >
                      <TableIcon className="h-4 w-4 text-blue-800" />
                    </button>
                  </div>
                </div>
              </div>

              {activeView === 'grid' ? (
                <DragDropContext onDragEnd={onDragEnd}>
                  <div className="flex flex-col sm:flex-row">
                    <div className="flex w-full max-w-[800px] flex-col pl-4 pr-6 sm:w-1/2">
                      {/* Requires Review */}
                      <h2 className="text-gold-dark text-md text-center font-bold">
                        Requires Review
                      </h2>

                      <Droppable droppableId="columnA">
                        {(provided, snapshot) => (
                          <div
                            className={`flex min-h-[400px] flex-col items-center gap-x-4 lg:flex-row lg:items-start ${
                              snapshot.isDraggingOver ? 'rounded-md bg-blue-300/80' : ''
                            }`}
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                          >
                            {renderRequiresReviewCards()}

                            <div className="h-px overflow-hidden">{provided.placeholder}</div>
                          </div>
                        )}
                      </Droppable>

                      {isROServiceUser ? (
                        <Pagination
                          totalItems={dealerTotalRows}
                          itemsPerPage={dealerPerPage}
                          currentPage={dealerCurrentPage}
                          setCurrentPage={setDealerCurrentPage}
                          onPageChange={handleDealerReviewPagination}
                        />
                      ) : (
                        <Pagination
                          totalItems={adminTotalRows}
                          itemsPerPage={adminPerPage}
                          currentPage={adminCurrentPage}
                          setCurrentPage={setAdminCurrentPage}
                          onPageChange={handleAdminReviewPagination}
                        />
                      )}
                    </div>

                    <div className="flex w-full max-w-[800px] flex-col pl-6 sm:w-1/2">
                      {/* Waiting on Response */}
                      <h2 className="text-md text-center font-bold text-blue-950">
                        Waiting on{' '}
                        {isROServiceUser ? `${adminStatusPrefix} Feedback` : 'Dealer Response'}
                      </h2>

                      <Droppable droppableId="columnB">
                        {(provided, snapshot) => (
                          <div
                            className={mergeClassNames(
                              `flex min-h-[400px] flex-col items-center gap-x-4 lg:flex-row lg:items-start ${
                                snapshot.isDraggingOver ? 'rounded-md bg-blue-300/80' : ''
                              }`,
                            )}
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                          >
                            {renderWaitingOnResponseCards()}

                            <div className="h-px overflow-hidden">{provided.placeholder}</div>
                          </div>
                        )}
                      </Droppable>

                      {isROServiceUser ? (
                        <Pagination
                          totalItems={adminTotalRows}
                          itemsPerPage={adminPerPage}
                          currentPage={adminCurrentPage}
                          setCurrentPage={setAdminCurrentPage}
                          onPageChange={handleAdminReviewPagination}
                        />
                      ) : (
                        <Pagination
                          totalItems={dealerTotalRows}
                          itemsPerPage={dealerPerPage}
                          currentPage={dealerCurrentPage}
                          setCurrentPage={setDealerCurrentPage}
                          onPageChange={handleDealerReviewPagination}
                        />
                      )}
                    </div>
                  </div>
                </DragDropContext>
              ) : (
                <div className="space-y-4">
                  {/* Requires Review */}
                  <div className="flex flex-col gap-2">
                    <span className="text-gold-dark text-md font-bold">Requires Review</span>

                    {isROServiceUser
                      ? renderWaitingOnDealerResponseTable()
                      : renderWaitingOnWpcResponseTable()}
                  </div>

                  {/* Waiting on Response */}
                  <div className="flex flex-col gap-2">
                    <span className="text-md font-bold text-blue-950">
                      Waiting on{' '}
                      {isROServiceUser ? `${adminStatusPrefix} Feedback` : 'Dealer Response'}
                    </span>

                    {isROServiceUser
                      ? renderWaitingOnWpcResponseTable()
                      : renderWaitingOnDealerResponseTable()}
                  </div>
                </div>
              )}
            </div>
          </CollapsibleSection>

          {/* Resolved Repair Orders */}
          <ResolvedROs dealerId={id} dealer={updatedDealer} dealerName={updatedDealer?.name} />
        </div>
      </StateContainer>

      <Modal
        open={isConfirmationModalVisible}
        title="Are you sure?"
        setOpen={setIsConfirmationModalVisible}
        content={
          <div className="my-8 flex h-full items-center justify-center">
            <p className="text-center">
              {`Please confirm that you'd like to return the repair order to ${
                repairOrderPayload.markAsDealerReview ? 'Dealer' : 'WPC'
              }.`}
            </p>
          </div>
        }
        actions={[
          {
            type: 'submit',
            label: 'Confirm',
            onClick: async () => {
              // Call the update function
              await updateRepairOrder(
                id,
                selectedRepairOrderId,
                repairOrderPayload,
                setLoadingOutstanding,
                handleErrors,
                () => {
                  handleSuccess(
                    `Repair order returned to ${
                      repairOrderPayload.markAsDealerReview ? 'Dealer' : 'WPC'
                    }.`,
                  )

                  // Update the KPIs and repair orders
                  getUpdatedRepairOrderKpis(`${KPI_BASE_URL}?${urlParams}`)
                  const params = urlParams === '' ? '' : `&${urlParams}`
                  getUpdatedRepairOrders(
                    `${RO_BASE_URL}&page=1&order_by=${adminSortedColumn}${params}`,
                    'WPC Review',
                  )
                  getUpdatedRepairOrders(
                    `${RO_BASE_URL}&page=1&order_by=${dealerSortedColumn}${params}`,
                    'Dealer Review',
                  )

                  setIsConfirmationModalVisible(false)
                },
              )
            },
          },
          { type: 'cancel', label: 'Cancel' },
        ]}
      />
    </>
  )
})

export default DealerDetails
